import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { musicalService } from "../../services/musicalService";
import { songService } from "../../services/songService";
import { userService } from "../../services/userService";
import { RoleService as roleService } from "../../services/roleService";
import { useAuth } from "../../hooks/useAuth";
import { useMultiTableRealtimeSync } from "../../hooks/useRealtimeSync";
import Header from "../../components/ui/Header";
import MusicalForm from "./components/MusicalForm";
import CharacterSection from "./components/CharacterSection";
import { SongsSection } from './components/SongsSection';
import Icon from '../../components/AppIcon';
import SuccessNotification from '../../components/ui/SuccessNotification';
import Button from '../../components/ui/Button';




export default function AddMusicalPage() {
  const [musical, setMusical] = useState(null);
  const [songs, setSongs] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    year_premiered: '',
    composer: '',
    lyricist: '',
    book_writer: '',
    published_by: '',
    rights_group: '',
    description: '',
    genre: 'broadway',
    poster_url: ''
  });
  const [posterFile, setPosterFile] = useState(null);
  const [posterPreview, setPosterPreview] = useState('');
  const [error, setError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isFavorite, setIsFavorite] = useState(false);
  const [checkingFavorite, setCheckingFavorite] = useState(false);
  const [isFeatured, setIsFeatured] = useState(false);
  const [checkingFeatured, setCheckingFeatured] = useState(false);

  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  // ✅ Real-time sync for multiple tables (musicals, songs, roles)
  useMultiTableRealtimeSync([
    {
      table: 'musicals',
      filter: id ? { column: 'id', value: id } : null,
      onUpdate: (updatedMusical) => {
        console.log('Musical updated in real-time:', updatedMusical);
        if (updatedMusical?.id === id) {
          setMusical(prev => ({ ...prev, ...updatedMusical }));
        }
      }
    },
    {
      table: 'songs',
      filter: id ? { column: 'musical_id', value: id } : null,
      onInsert: (newSong) => {
        console.log('New song added to musical:', newSong);
        if (newSong?.musical_id === id) {
          fetchSongs();
        }
      },
      onUpdate: (updatedSong) => {
        console.log('Song updated in musical:', updatedSong);
        if (updatedSong?.musical_id === id) {
          setSongs(prev => 
            prev?.map(song => song?.id === updatedSong?.id ? { ...song, ...updatedSong } : song)
          );
        }
      },
      onDelete: (deletedSong) => {
        console.log('Song deleted from musical:', deletedSong);
        setSongs(prev => prev?.filter(song => song?.id !== deletedSong?.id));
      }
    },
    {
      table: 'roles',
      filter: id ? { column: 'musical_id', value: id } : null,
      onInsert: (newRole) => {
        console.log('New role added to musical:', newRole);
        if (newRole?.musical_id === id) {
          fetchRoles();
        }
      },
      onUpdate: (updatedRole) => {
        console.log('Role updated in musical:', updatedRole);
        if (updatedRole?.musical_id === id) {
          setRoles(prev => 
            prev?.map(role => role?.id === updatedRole?.id ? { ...role, ...updatedRole } : role)
          );
        }
      },
      onDelete: (deletedRole) => {
        console.log('Role deleted from musical:', deletedRole);
        setRoles(prev => prev?.filter(role => role?.id !== deletedRole?.id));
      }
    }
  ]);

  // Fetch musical data
  const fetchMusicalData = async () => {
    if (!id) return;

    try {
      const { data: existingMusical, error: fetchError } = await musicalService?.getMusicalById(id);

      if (fetchError) {
        setError('Failed to load musical data. Please try again.');
        return;
      }

      if (existingMusical) {
        setMusical(existingMusical);
        setFormData({
          title: existingMusical?.title || '',
          year_premiered: existingMusical?.year_premiered?.toString() || '',
          composer: existingMusical?.composer || '',
          lyricist: existingMusical?.lyricist || '',
          book_writer: existingMusical?.book_writer || '',
          published_by: existingMusical?.published_by || '',
          rights_group: existingMusical?.rights_group || '',
          description: existingMusical?.description || '',
          genre: existingMusical?.genre || 'broadway',
          poster_url: existingMusical?.poster_url || ''
        });

        // Set poster preview - add timestamp to avoid caching
        if (existingMusical?.poster_url) {
          const timestampedUrl = existingMusical?.poster_url?.includes('?') 
            ? `${existingMusical?.poster_url}&t=${Date.now()}`
            : `${existingMusical?.poster_url}?t=${Date.now()}`;
          setPosterPreview(timestampedUrl);
        }

        // Load existing characters
        if (existingMusical?.roles?.length > 0) {
          setRoles(existingMusical?.roles?.map(role => ({
            id: role?.id,
            character_name: role?.character_name
          })));
        }

        // Load existing songs
        if (existingMusical?.songs?.length > 0) {
          // Deduplicate songs by normalized title before setting state
          const uniqueSongsMap = new Map();
          existingMusical?.songs?.forEach(song => {
            const normalizedTitle = song?.title?.toLowerCase()?.trim();
            if (!uniqueSongsMap?.has(normalizedTitle)) {
              uniqueSongsMap?.set(normalizedTitle, {
                id: song?.id,
                title: song?.title,
                tempId: song?.id,
                act_number: song?.act_number || 1,
                song_order: song?.song_order || 1,
                song_roles: song?.song_roles || []
              });
            }
          });
          
          setSongs(Array?.from(uniqueSongsMap?.values()));
        }
      }
    } catch (err) {
      console.error('Load musical error:', err);
      setError('An unexpected error occurred while loading the musical.');
    }
  };

  // Fetch songs for this musical
  const fetchSongs = async () => {
    if (!id) return;

    try {
      const { data: songsData, error: fetchError } = await songService?.getSongsByMusicalId(id);
      
      if (fetchError) {
        setError('Failed to load songs. Please try again.');
        return;
      }

      if (songsData?.length > 0) {
        // Deduplicate songs by normalized title
        const uniqueSongsMap = new Map();
        songsData?.forEach(song => {
          const normalizedTitle = song?.title?.toLowerCase()?.trim();
          if (!uniqueSongsMap?.has(normalizedTitle)) {
            uniqueSongsMap?.set(normalizedTitle, {
              id: song?.id,
              title: song?.title,
              tempId: song?.id,
              act_number: song?.act_number || 1,
              song_order: song?.song_order || 1,
              song_roles: song?.song_roles || []
            });
          }
        });
        
        setSongs(Array?.from(uniqueSongsMap?.values()));
      }
    } catch (err) {
      console.error('Error fetching songs:', err);
      setError('Failed to load songs. Please try again.');
    }
  };

  // Fetch roles for this musical
  const fetchRoles = async () => {
    if (!id) return;

    try {
      const { data: rolesData, error: fetchError } = await roleService?.getRolesByMusical(id);
      
      if (fetchError) {
        setError('Failed to load roles. Please try again.');
        return;
      }

      if (rolesData?.length > 0) {
        setRoles(rolesData?.map(role => ({
          id: role?.id,
          character_name: role?.character_name
        })));
      }
    } catch (err) {
      console.error('Error fetching roles:', err);
      setError('Failed to load roles. Please try again.');
    }
  };

  useEffect(() => {
    if (id) {
      fetchMusicalData();
      fetchSongs();
      fetchRoles();
      checkFavoriteStatus();
      checkFeaturedStatus();
    }
  }, [id]);

  const checkFeaturedStatus = async () => {
    if (!id) return;
    try {
      const { data } = await musicalService?.getMusicalById(id);
      setIsFeatured(data?.is_featured || false);
    } catch (err) {
      console.error('Error checking featured status:', err);
    }
  };

  const handleToggleFeatured = async () => {
    if (!id) return;
    try {
      setCheckingFeatured(true);
      const newFeaturedState = !isFeatured;
      const { error: updateError } = await musicalService?.updateMusical(id, {
        is_featured: newFeaturedState,
        updated_by: user?.id
      });
      if (updateError) throw updateError;
      setIsFeatured(newFeaturedState);
      setSuccessMessage(newFeaturedState ? 'Musical featured' : 'Musical unfeatured');
      setShowSuccess(true);
    } catch (err) {
      console.error('Error toggling featured:', err);
      setError(err?.message || 'Failed to update featured status');
    } finally {
      setCheckingFeatured(false);
    }
  };

  const checkFavoriteStatus = async () => {
    if (!user?.id || !id) return;

    try {
      setCheckingFavorite(true);
      const { isFavorite: favoriteStatus } = await userService?.isFavoriteMusical(user?.id, id);
      setIsFavorite(favoriteStatus);
    } catch (err) {
      console.error('Error checking favorite status:', err);
    } finally {
      setCheckingFavorite(false);
    }
  };

  const handleToggleFavorite = async () => {
    if (!user?.id || !id) return;

    try {
      setCheckingFavorite(true);

      if (isFavorite) {
        // Remove from favorites
        const { error: removeError } = await userService?.removeFavoriteMusical(user?.id, id);
        if (removeError) throw removeError;

        setIsFavorite(false);
        setSuccessMessage('Removed from favorites');
      } else {
        // Add to favorites
        const { error: addError } = await userService?.addFavoriteMusical(user?.id, id);
        if (addError) throw addError;

        setIsFavorite(true);
        setSuccessMessage('Added to favorites');
      }

      setShowSuccess(true);
    } catch (err) {
      console.error('Error toggling favorite:', err);
      setError(err?.message || 'Failed to update favorites');
    } finally {
      setCheckingFavorite(false);
    }
  };

  const handleFormChange = (data) => {
    setFormData(prev => ({
      ...prev,
      ...data
    }));
  };

  const handleSongsChange = (updatedSongs) => {
    setSongs(updatedSongs);
  };

  const handleCharactersChange = (updatedCharacters) => {
    setRoles(updatedCharacters);
  };

  const handlePosterChange = (file, preview) => {
    setPosterFile(file);
    setPosterPreview(preview);
  };

  const validateRequiredFields = () => {
    if (!formData?.title?.trim()) {
      throw new Error('Musical Name is required');
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    try {
      setLoading(true);
      setError(null);
      setSuccessMessage('');

      let musicalData = { ...formData };

      // CRITICAL FIX: Upload poster BEFORE updating musical
      if (posterFile) {
        try {
          const { data: uploadResult, error: uploadError } = await musicalService?.uploadPoster(
            posterFile,
            id
          );

          if (uploadError) {
            throw new Error(`Poster upload failed: ${uploadError?.message}`);
          }

          // Update musicalData with the new poster URL
          musicalData.poster_url = uploadResult?.publicUrl;
          
          // Update preview to show the new URL
          setPosterPreview(uploadResult?.publicUrl);
          
        } catch (uploadErr) {
          console.error('Poster upload error:', uploadErr);
          setError(`Failed to upload poster: ${uploadErr?.message}`);
          return; // Stop submission if poster upload fails
        }
      }

      if (id) {
        // Update existing musical with poster URL
        await musicalService?.updateMusical(id, {
          ...musicalData,
          updated_by: user?.id
        });
        
        // Save songs to database
        if (songs?.length > 0) {
          await saveSongsToDatabase(id, songs);
        }
        
        setSuccessMessage('Musical updated successfully!');
        setShowSuccess(true);
      } else {
        // Create new musical
        const newMusical = await musicalService?.createMusical(musicalData);
        
        // Save songs to database
        if (songs?.length > 0 && newMusical?.id) {
          await saveSongsToDatabase(newMusical?.id, songs);
        }
        
        setSuccessMessage('Musical created successfully!');
        setShowSuccess(true);
        
        // Redirect to edit page after creation
        setTimeout(() => {
          navigate(`/add-musical/${newMusical?.id}`);
        }, 1500);
      }
    } catch (err) {
      console.error('Error saving musical:', err);
      setError(err?.message || 'Failed to save musical. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const saveSongsToDatabase = async (musicalId, songsToSave) => {
    try {
      // Get existing songs for this musical to check for duplicates
      const existingSongs = await songService?.getSongsByMusicalId(musicalId);
      const existingSongTitles = new Map(
        existingSongs?.map(song => [song?.title?.toLowerCase()?.trim(), song?.id])
      );

      for (const song of songsToSave) {
        const normalizedTitle = song?.title?.toLowerCase()?.trim();
        
        // Check if song already exists in database (has a real ID, not temp)
        if (song?.id && !song?.id?.toString()?.startsWith('temp_')) {
          // Update existing song
          await songService?.updateSong(song?.id, {
            title: song?.title,
            musical_id: musicalId,
            act_number: song?.act_number,
            song_order: song?.song_order,
            updated_by: user?.id
          });
        } else {
          // Check if a song with this title already exists for this musical
          const existingSongId = existingSongTitles?.get(normalizedTitle);
          
          if (existingSongId) {
            // Song already exists, update it instead of creating a duplicate
            await songService?.updateSong(existingSongId, {
              title: song?.title,
              musical_id: musicalId,
              act_number: song?.act_number,
              song_order: song?.song_order,
              updated_by: user?.id
            });
          } else {
            // Create new song
            const newSong = await songService?.createSong({
              title: song?.title,
              musical_id: musicalId,
              act_number: song?.act_number,
              song_order: song?.song_order,
              created_by: user?.id
            });
            
            // Add the newly created song to our existing songs map to prevent duplicates in this batch
            if (newSong?.id) {
              existingSongTitles?.set(normalizedTitle, newSong?.id);
            }
          }
        }
      }
    } catch (err) {
      console.error('Error saving songs:', err);
      throw new Error(`Failed to save songs to database: ${err?.message}`);
    }
  };

  const handleCancel = () => {
    navigate('/all-musicals');
  };

  const handleClearForm = () => {
    const confirmation = window?.confirm('Are you sure you want to clear all form data? This action cannot be undone.');
    if (confirmation) {
      setFormData({
        title: '',
        year_premiered: '',
        composer: '',
        lyricist: '',
        book_writer: '',
        published_by: '',
        rights_group: '',
        description: '',
        genre: 'broadway',
        poster_url: ''
      });
      setRoles([{ id: Date.now(), character_name: '' }]);
      setSongs([{ id: Date.now(), title: '', tempId: Date.now() }]);
      setPosterFile(null);
      setPosterPreview('');
      setError(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900">
        <Header />
        <main className="container mx-auto px-6 py-8 max-w-4xl">
          <div className="space-y-6">
            {Array?.from({ length: 6 })?.map((_, i) => (
              <div key={i} className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6">
                <div className="h-6 bg-white bg-opacity-20 rounded-xl shimmer mb-4 w-1/3"></div>
                <div className="space-y-3">
                  <div className="h-4 bg-white bg-opacity-20 rounded-xl shimmer w-full"></div>
                  <div className="h-4 bg-white bg-opacity-20 rounded-xl shimmer w-3/4"></div>
                  <div className="h-4 bg-white bg-opacity-20 rounded-xl shimmer w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        </main>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{id ? 'Edit Musical' : 'Add New Musical'} - RoleCall</title>
        <meta name="description" content={id ? 'Edit musical details, songs, and characters' : 'Add a new musical with songs and characters'} />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900">
        <Header />
        <main className="container mx-auto px-6 py-8 max-w-6xl">
          {/* Breadcrumb Navigation */}
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 text-blue-200 text-sm font-caption">
                <button
                  onClick={() => navigate('/all-musicals')}
                  className="hover:text-white transition-colors duration-200 flex items-center space-x-1"
                >
                  <Icon name="ArrowLeft" size={14} />
                  <span>All Musicals</span>
                </button>
                <Icon name="ChevronRight" size={14} />
                <span className="text-white">{id ? 'Edit Musical' : 'Add Musical'}</span>
              </div>

              {/* Feature & Favorite Buttons */}
              {id && (
                <div className="flex items-center space-x-2">
                  {/* Star = Feature Musical */}
                  <button
                    onClick={handleToggleFeatured}
                    disabled={checkingFeatured}
                    className={`p-3 rounded-full transition-all duration-200 ${
                      isFeatured
                        ? 'bg-yellow-400 hover:bg-yellow-500' :'bg-white bg-opacity-10 hover:bg-opacity-20'
                    } ${checkingFeatured ? 'opacity-50 cursor-not-allowed' : ''}`}
                    title={isFeatured ? 'Unfeature Musical' : 'Feature Musical'}
                  >
                    <Icon
                      name="Star"
                      size={24}
                      className={`${isFeatured ? 'text-indigo-900 fill-current' : 'text-white'}`}
                    />
                  </button>

                  {/* Heart = Favorite */}
                  <button
                    onClick={handleToggleFavorite}
                    disabled={checkingFavorite}
                    className={`p-3 rounded-full transition-all duration-200 ${
                      isFavorite
                        ? 'bg-red-500 hover:bg-red-600' :'bg-white bg-opacity-10 hover:bg-opacity-20'
                    } ${checkingFavorite ? 'opacity-50 cursor-not-allowed' : ''}`}
                    title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                  >
                    <Icon
                      name="Heart"
                      size={24}
                      className={`${isFavorite ? 'text-white fill-current' : 'text-white'}`}
                    />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-heading font-bold text-white mb-2">
              {id ? 'Edit Musical' : 'Add New Musical'}
            </h1>
            <p className="text-blue-100 font-body">
              {id 
                ? 'Update musical information, manage songs and characters'
                : 'Create a new musical entry with songs and character information'
              }
            </p>
          </div>

          {/* Success Notification */}
          {showSuccess && (
            <SuccessNotification
              message={successMessage}
              isOpen={showSuccess}
              isVisible={showSuccess}
              onClose={() => setShowSuccess(false)}
            />
          )}

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 bg-opacity-90 border border-red-200 rounded-xl p-6 mb-6">
              <div className="flex items-center space-x-2">
                <Icon name="AlertTriangle" size={20} className="text-red-800" />
                <p className="text-red-800 font-medium">{error}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Musical Details Form */}
            <MusicalForm
              data={formData}
              onChange={handleFormChange}
              onPosterChange={handlePosterChange}
              posterPreview={posterPreview}
              errors={{}}
            />

            {/* Songs Section */}
            <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 rounded-lg p-6 border border-gray-700/50">
              <SongsSection 
                musicalId={id} 
                initialSongs={songs}
                onChange={handleSongsChange}
              />
            </div>

            {/* Characters Section */}
            <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 rounded-lg p-6 border border-gray-700/50">
              <CharacterSection 
                musicalId={id} 
                characters={roles}
                onChange={handleCharactersChange}
              />
            </div>

            {/* Submit Button */}
            <div className="flex justify-end gap-4">
              <Button
                type="button"
                onClick={() => navigate('/all-musicals')}
                variant="outline"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              >
                {loading ? 'Saving...' : id ? 'Update Musical' : 'Create Musical'}
              </Button>
            </div>
          </form>
        </main>
      </div>
    </>
  );
}